import { ContentfulEntry, ContentfulVideoFields } from '@devtinker/shared';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getEntries } from '../lib/contentful';

interface CategoryCount {
  name: string;
  count: number;
}

const Videos: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [videos, setVideos] = useState<ContentfulEntry<ContentfulVideoFields>[]>([]);
  const [allVideos, setAllVideos] = useState<ContentfulEntry<ContentfulVideoFields>[]>([]);
  const [categories, setCategories] = useState<CategoryCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState<ContentfulEntry<ContentfulVideoFields> | null>(null);
  const selectedCategory = searchParams.get('category');

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const entries = await getEntries('video');
        const sortedEntries = (entries as ContentfulEntry<ContentfulVideoFields>[])
          .sort((a, b) => new Date(b.fields.publishDate || 0).getTime() - new Date(a.fields.publishDate || 0).getTime());
        setAllVideos(sortedEntries);
        setVideos(sortedEntries);
        
        // Extract categories from all videos
        const categoryMap = new Map<string, number>();
        sortedEntries.forEach(video => {
          const category = video.fields.category;
          if (category) {
            categoryMap.set(category, (categoryMap.get(category) || 0) + 1);
          }
        });
        
        const extractedCategories = Array.from(categoryMap.entries())
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count);
        setCategories(extractedCategories);
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // Filter videos when category changes
  useEffect(() => {
    if (selectedCategory) {
      const filtered = allVideos.filter(video => 
        video.fields.category === selectedCategory
      );
      setVideos(filtered);
    } else {
      setVideos(allVideos);
    }
  }, [selectedCategory, allVideos]);

  // Mock data for when Contentful is not configured
  const mockVideos = [
    {
      sys: { id: '1', type: 'Entry', createdAt: '2021-06-01', updatedAt: '2021-06-01', contentType: { sys: { id: 'video' } } },
      fields: {
        title: 'DevTinker Introduction',
        slug: 'devtinker-intro',
        description: 'An introduction to DevTinker and our mission to build, develop, and tinker with modern technologies.',
        embedUrl: 'https://player.vimeo.com/video/435946600',
        embedType: 'vimeo' as const,
        duration: '2:30',
        publishDate: '2021-06-01',
        category: 'Introduction',
        tags: ['DevTinker', 'Introduction'],
        thumbnail: undefined
      }
    },
    {
      sys: { id: '2', type: 'Entry', createdAt: '2022-08-01', updatedAt: '2022-08-01', contentType: { sys: { id: 'video' } } },
      fields: {
        title: 'Productivity Tips & Tricks',
        slug: 'productivity-tips',
        description: 'Explore our favorite productivity methods and tools that help us stay organized and efficient.',
        embedUrl: 'https://player.vimeo.com/video/435946600',
        embedType: 'vimeo' as const,
        duration: '5:45',
        publishDate: '2022-08-01',
        category: 'Productivity',
        tags: ['Productivity', 'GTD', 'Tips'],
        thumbnail: undefined
      }
    },
    {
      sys: { id: '3', type: 'Entry', createdAt: '2023-01-15', updatedAt: '2023-01-15', contentType: { sys: { id: 'video' } } },
      fields: {
        title: 'Web Development Workflow',
        slug: 'web-dev-workflow',
        description: 'A walkthrough of our web development workflow from concept to deployment.',
        embedUrl: 'https://player.vimeo.com/video/435946600',
        embedType: 'vimeo' as const,
        duration: '8:20',
        publishDate: '2023-01-15',
        category: 'Development',
        tags: ['Web Development', 'Workflow', 'Tutorial'],
        thumbnail: undefined
      }
    },
    {
      sys: { id: '4', type: 'Entry', createdAt: '2023-09-10', updatedAt: '2023-09-10', contentType: { sys: { id: 'video' } } },
      fields: {
        title: 'Story Mapping Techniques',
        slug: 'story-mapping',
        description: 'Learn how to effectively use story mapping for project planning and team collaboration.',
        embedUrl: 'https://player.vimeo.com/video/435946600',
        embedType: 'vimeo' as const,
        duration: '6:15',
        publishDate: '2023-09-10',
        category: 'Project Management',
        tags: ['Story Mapping', 'Planning', 'Collaboration'],
        thumbnail: undefined
      }
    }
  ];

  const displayVideos = videos.length > 0 ? videos : mockVideos;

  const handleVideoClick = (video: ContentfulEntry<ContentfulVideoFields>) => {
    setSelectedVideo(video);
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  };

  const handleCloseModal = () => {
    setSelectedVideo(null);
    document.body.style.overflow = 'auto'; // Re-enable scrolling
  };

  const getEmbedUrl = (video: ContentfulEntry<ContentfulVideoFields>) => {
    let url = video.fields.embedUrl;
    
    // Add autoplay and other parameters based on embed type
    if (video.fields.embedType === 'vimeo') {
      url += url.includes('?') ? '&' : '?';
      url += 'autoplay=1&title=1&byline=1&portrait=1';
    } else if (video.fields.embedType === 'youtube') {
      url += url.includes('?') ? '&' : '?';
      url += 'autoplay=1&rel=0';
    }
    
    return url;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading videos...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Videos</h1>
          <p className="text-xl text-gray-600">Visual Medium Explorations</p>
          {selectedCategory && (
            <div className="mt-4 flex items-center justify-center gap-2">
              <span className="text-sm text-gray-600">Filtered by:</span>
              <span className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full font-medium">
                {selectedCategory}
                <button
                  onClick={() => setSearchParams({})}
                  className="hover:text-green-900"
                  aria-label="Clear filter"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            </div>
          )}
        </div>

        {/* Videos Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayVideos.map((video) => (
            <button
              key={video.sys.id}
              onClick={() => handleVideoClick(video)}
              className="group block text-left"
            >
              <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-200">
                {/* Thumbnail */}
                <div className="relative w-full h-48 overflow-hidden bg-gradient-to-br from-green-500 to-green-700">
                  {video.fields.thumbnail ? (
                    <img
                      src={video.fields.thumbnail.fields.file.url}
                      alt={video.fields.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg className="w-16 h-16 text-white opacity-50" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  )}
                  
                  {/* Play Button Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300 shadow-lg">
                      <svg className="w-8 h-8 text-green-600 ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>

                  {/* Duration Badge */}
                  {video.fields.duration && (
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                      {video.fields.duration}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Category/Tags */}
                  {video.fields.category && (
                    <span className="text-xs font-semibold uppercase tracking-wide text-green-600 bg-green-50 px-3 py-1 rounded-full inline-block mb-3">
                      {video.fields.category}
                    </span>
                  )}

                  {/* Title */}
                  <h2 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors line-clamp-2">
                    {video.fields.title}
                  </h2>

                  {/* Description */}
                  <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                    {video.fields.description}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Sidebar-like section */}
        <aside className="mt-16 bg-gray-50 p-8 rounded-lg">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Categories */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Categories</h3>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => setSearchParams({})}
                    className={`w-full flex items-center justify-between transition-colors ${
                      !selectedCategory ? 'text-green-600 font-semibold' : 'text-gray-700 hover:text-green-600'
                    }`}
                  >
                    <span>All Videos</span>
                    <span className="bg-white px-2 py-1 rounded text-sm border border-gray-200">
                      {allVideos.length}
                    </span>
                  </button>
                </li>
                {categories.map((category) => (
                  <li key={category.name}>
                    <button
                      onClick={() => setSearchParams({ category: category.name })}
                      className={`w-full flex items-center justify-between transition-colors ${
                        selectedCategory === category.name ? 'text-green-600 font-semibold' : 'text-gray-700 hover:text-green-600'
                      }`}
                    >
                      <span>{category.name}</span>
                      <span className="bg-white px-2 py-1 rounded text-sm border border-gray-200">
                        {category.count}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Recent Videos */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Recent Videos</h3>
              <ul className="space-y-3">
                {displayVideos.slice(0, 4).map((video) => (
                  <li key={video.sys.id}>
                    <button
                      onClick={() => handleVideoClick(video)}
                      className="text-gray-700 hover:text-green-600 transition-colors text-sm text-left"
                    >
                      {video.fields.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </aside>
      </div>

      {/* Video Player Modal */}
      {selectedVideo && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-90 p-4 overflow-y-auto"
          onClick={handleCloseModal}
        >
          <div className="min-h-screen flex items-center justify-center py-8">
            <div
              className="relative w-full max-w-5xl mx-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={handleCloseModal}
                className="absolute -top-10 right-0 text-white hover:text-green-400 transition-colors z-10"
                aria-label="Close video"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Video Container with 16:9 Aspect Ratio */}
              <div className="relative w-full bg-black rounded-lg overflow-hidden shadow-2xl" style={{ aspectRatio: '16/9' }}>
                <iframe
                  src={getEmbedUrl(selectedVideo)}
                  className="absolute top-0 left-0 w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title={selectedVideo.fields.title}
                />
              </div>

              {/* Video Info */}
              <div className="mt-4 text-white px-2">
                <h2 className="text-2xl font-bold mb-2">{selectedVideo.fields.title}</h2>
                <p className="text-gray-300 text-sm">{selectedVideo.fields.description}</p>
                {selectedVideo.fields.tags && selectedVideo.fields.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {selectedVideo.fields.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="text-xs font-semibold uppercase tracking-wide text-green-400 bg-green-900 bg-opacity-50 px-3 py-1 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Videos;
